import {
  type InferGetStaticPropsType,
  type GetStaticPaths,
  type NextPage,
  type GetStaticPropsContext,
} from "next";
import Image from "next/image";
import Layout from "~/components/Layout";
import { type LoopPostReturnType } from "~/lib/db/loops";
import {
  getArticleFromNumber,
  getImgageCurrentNumbersPreviusNumber,
} from "~/lib/db/previousNumbers";
import { getThumbnail, getPostsMetadata } from "lib/db/posts";
import { ISSUES_TYPE, type ArticlePreviewType } from "~/types";
import { postExcerpt } from "~/utils";
import pdf from "@public/icon/ico-pdf.svg";
import { useSession } from "next-auth/react";
import { getCategoriesSlug } from "~/lib/db/category";
import { useRouter } from "next/router";
import { getPdfNumber } from "~/lib/db/pdf";
import TitleLoop from "~/components/TitleLoop";
import NumeroCorrente from "~/components/loop/NumeroCorrente";
import Print from "~/components/Article/Print";
import { creaHtmlPost, setProtectedUrlPDF } from "~/utils/postUtils";
import NumberMegazineStampa from "~/components/NumberMegazineStampa";
import { protectedUrlPdf } from "~/lib/buildPost";

const NumberMegazine: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = (params) => {
  const session = useSession();
  const isAuthenticated = session?.status === "authenticated";
  const router = useRouter();
  const number = router.query.number as string;
  return (
    <>
      <Layout title={number}>
        <section className="mx-auto mb-32 max-w-box px-4 print:hidden">
          {isAuthenticated && (
            <div className="mb-20 mt-12 flex justify-end md:mb-32">
              <div className="mx-4 flex gap-10">
                {params.idPdf != null && (
                  <div className="flex items-center gap-8 hover:cursor-pointer">
                    <a
                      id="export-pdf"
                      target="_blank"
                      className="flex items-center gap-1 hover:cursor-pointer hover:underline"
                      /*onClick={() => downloadPdf()}*/
                      href={`/api/downloadPdf?number=${number}&id=${params.idPdf}`}
                    >
                      <Image src={pdf} alt="PDF" />
                      <span className="hidden md:block">PDF</span>
                    </a>
                  </div>
                )}
                <div className="flex items-center gap-8 hover:cursor-pointer">
                  <Print />
                </div>
              </div>
            </div>
          )}
          <div className="flex w-full flex-col gap-y-16 md:flex-row md:gap-x-12 lg:gap-x-24 xl:gap-x-36">
            <div className="numeroCorrenteMain flex md:basis-[35%]">
              {Boolean(params.imageNumber?.img) && (
                <figure className="w-full">
                  <div className="bg-lightLightGray md:sticky md:top-[105px]">
                    <Image
                      width={453}
                      height={582}
                      src={params.imageNumber.img}
                      alt={params.imageNumber.description}
                      className="w-full object-cover mix-blend-multiply shadow-[0_0_30px_#d5cece] "
                      sizes="(max-width: 767px) 100vw, 33vw"
                      priority={true}
                    />
                  </div>
                </figure>
              )}
            </div>
            <div className="flex md:basis-[65%]">
              <div className="flex w-full flex-col justify-start md:pt-6">
                <TitleLoop
                  titleLoop={params.number}
                  customClass={
                    "text-[38px] sm:text-5xl text-[#7b7f86] font-bold uppercase"
                  }
                />
                <NumeroCorrente numeroCorrente={params.articles} />
              </div>
            </div>
          </div>
        </section>
        {isAuthenticated && (
          <div className="hidden print:block">
            <NumberMegazineStampa
              articles={params.articlesPrint}
              numberMagazine={params.number}
            />
          </div>
        )}
      </Layout>
    </>
  );
};

export default NumberMegazine;

export const getStaticPaths: GetStaticPaths<{ number: string }> = async () => {
  const categoriesSlug = await getCategoriesSlug(ISSUES_TYPE.numbers, 62);

  return {
    paths: categoriesSlug.map((category) => ({
      params: { number: category.slug },
    })), //indicates that no page needs be created at build time
    fallback: "blocking", //indicates the type of fallback
  };
};

export const getStaticProps = async ({ params }: GetStaticPropsContext) => {
  if (!params) return { notFound: true };
  try {
    const [articles, pdf, imageNumber] = await Promise.all([
      getArticleFromNumber(params.number as string),
      getPdfNumber(params.number as string),
      getImgageCurrentNumbersPreviusNumber(params.number as string),
    ]);
    if (articles.length == 0) return { notFound: true };
    const articleId = articles.map((el) => {
      return el.ID;
    });

    articles?.map(async (el, index) => {
      const protectedUrlPDF = (await protectedUrlPdf(el?.post_content)) || [];
      articles[index].post_content = setProtectedUrlPDF(
        el?.post_content,
        protectedUrlPDF
      );
    });
    const [metaData, thumbnail] = await Promise.all([
      articleId.length > 0 ? await getPostsMetadata(articleId) : [],
      articleId.length > 0 ? await getThumbnail(articleId) : [],
    ]);

    const mapPostsForClient = (
      posts: LoopPostReturnType[]
    ): ArticlePreviewType[] =>
      posts.map(
        ({
          ID,
          post_title,
          post_name,
          post_excerpt,
          post_content,
          post_date,
        }) => ({
          post_name,
          post_title,
          post_excerpt:
            post_excerpt != ""
              ? postExcerpt(post_excerpt)
              : postExcerpt(
                  post_content
                    ?.replace(/<\/?[^>]+(>|$)/g, "")
                    ?.replace(/\[\/{0,1}et_pb_.*\]/g, "")
                ),
          meta: metaData?.[Number(ID)] || null,
          thumbnail: thumbnail?.[Number(ID)]?.thumbnail || null,
          alt: thumbnail?.[Number(ID)]?.alt || null,
          post_date: post_date.toISOString(),
        })
      );

    const mapPostsForClientPrint = (
      posts: LoopPostReturnType[]
    ): ArticlePreviewType[] =>
      posts.map(({ ID, post_title, post_name, post_content, post_date }) => ({
        post_name,
        post_title,
        post_content,
        post_excerpt: creaHtmlPost(post_content, [], ""),
        meta: metaData?.[Number(ID)] || null,
        thumbnail: thumbnail?.[Number(ID)]?.thumbnail || null,
        alt: thumbnail?.[Number(ID)]?.alt || null,
        post_date: post_date.toISOString(),
      }));
    return {
      props: {
        number: params.number as string,
        articles: mapPostsForClient(articles),
        articlesPrint: mapPostsForClientPrint(articles),
        idPdf: pdf == null ? null : pdf,
        imageNumber: imageNumber.length == 0 ? null : imageNumber[0],
      },
      revalidate: 20 * 60,
    };
  } catch (error) {
    console.log("Error fetching static props for", params.number, error);
    return { notFound: true };
  }
};
